import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { SearchableDropdown, Button, SideBarModal } from '@/components';
import { StepIndicator } from '@/components/StepIndicator';
import { showErrorToast } from '@/components/toast/globalToast';
import { IssueSeverity, Handover } from '@/types/common';
import { getListHandover } from '@/services/api/handovers';
import { v4 as uuidv4 } from 'uuid';
import { getBucketUrlImageV2, uploadFileImage } from '@/utils';

export interface IssueFormData {
  handoverID: string;
  vehiclePlatNomor: string;
  issueType: string;
  incidentDate: string;
  severity: IssueSeverity;
  estimatedCost: number;
  costBearer: string;
  mitraName?: string;
  clientName?: string;
  vehicleOemName?: string;
  oemID?: string;
  driverID?: string;
  driverCode?: string;
  driverName?: string;
  clientID?: number;

  // Step 2
  photos: File[];
  imageURL?: string;
  notes: string;
}

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: IssueFormData) => Promise<void>;
  isLoading?: boolean;
}

// Custom hook for fetching data (similar to RepoModal)
const useIssueFormData = () => {
  const [handovers, setHandovers] = useState<Handover[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch active handovers to populate Plate Number list
      const response = await getListHandover({
        page: '1',
        size: '999',
        statuses: 'ACTIVE',
        isAvailable: 'true',
      });
      setHandovers(response.data || []);
    } catch (error) {
      console.error('Error loading form data:', error);
      setHandovers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { handovers, isLoading, loadData };
};

// Issue Type Options
const issueTypeOptions = [
  {
    value: 'Kerusakan akibat Kecelakaan',
    label: 'Kerusakan akibat Kecelakaan',
  },
  {
    value: 'Kerusakan akibat Penggunaan',
    label: 'Kerusakan akibat Penggunaan',
  },
  // { value: 'Kehilangan Unit', label: 'Kehilangan Unit' },
];

const severityOptions = [
  { value: IssueSeverity.LOW, label: 'Low' },
  { value: IssueSeverity.MEDIUM, label: 'Medium' },
  { value: IssueSeverity.HIGH, label: 'High' },
  { value: IssueSeverity.CRITICAL, label: 'Critical' },
];

export const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<IssueFormData>({
    handoverID: '',
    vehiclePlatNomor: '',
    issueType: '',
    incidentDate: '',
    severity: '' as IssueSeverity,
    estimatedCost: 0,
    costBearer: '',
    mitraName: '',
    clientName: '',
    vehicleOemName: '',
    photos: [],
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // IDR display state for dot separator
  const [idrDisplayCost, setIdrDisplayCost] = useState('');

  const handleCostChange = (raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    const numValue = parseInt(digitsOnly, 10) || 0;
    const formatted = numValue === 0 ? '' : numValue.toLocaleString('id-ID');
    setIdrDisplayCost(formatted);
    setFormData((prev) => ({ ...prev, estimatedCost: numValue }));
  };

  const { handovers, isLoading: isLoadingData, loadData } = useIssueFormData();

  useEffect(() => {
    if (isOpen) {
      loadData();
      setCurrentStep(1); // Reset to step 1
      // Reset form data...
      setFormData({
        handoverID: '',
        vehiclePlatNomor: '',
        issueType: '',
        incidentDate: '',
        severity: '' as IssueSeverity,
        estimatedCost: 0,
        costBearer: '',
        mitraName: '',
        photos: [],
        notes: '',
      });
      setIdrDisplayCost('');
    }
  }, [isOpen, loadData]);

  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    // Basic validation for Step 1 could go here
    setCurrentStep(2);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let uploadedUrl = '';

      // Handle Image Upload
      if (formData.photos && formData.photos.length > 0) {
        const file = formData.photos[0];
        const uuid = uuidv4();

        // 1. Get Bucket URL
        const bucketRes = await getBucketUrlImageV2({
          uuid,
          type: 'issue',
        });
        const url = bucketRes.data;

        // 2. Convert to Base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        const base64Data = base64.split(',')[1];

        // 3. Upload File
        const uploadRes = await uploadFileImage(url, base64Data, file.name);

        if (uploadRes.status === 200) {
          uploadedUrl = url;
        } else {
          throw new Error('Image upload failed');
        }
      }

      // Submit with imageURL
      await onSubmit({
        ...formData,
        imageURL: uploadedUrl,
      });
    } catch (error) {
      console.error(error);
      showErrorToast('Error', 'Failed to process request. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Options ---
  const handoverOptions = useMemo(
    () =>
      handovers
        .filter((h) => h.ID)
        .map((h) => ({
          value: h.ID,
          label: `${h.platNomor} - ${h.oemName} - ${h.modelName}`,
          data: h,
        })),
    [handovers]
  );

  // Derived values
  const selectedHandover = useMemo(
    () => handovers.find((h) => h.ID === formData.handoverID),
    [handovers, formData.handoverID]
  );

  // Sync external names to formData based on selected handover
  useEffect(() => {
    if (selectedHandover) {
      setFormData((prev) => ({
        ...prev,
        mitraName: `${selectedHandover.driverCode || ''} - ${selectedHandover.driverName || 'No Name'}`,
        clientName:
          selectedHandover.clientName || selectedHandover.client || '',
        vehicleOemName: selectedHandover.oemName || '',
        oemID: selectedHandover.oemID || '',
        driverID: selectedHandover.driverID || undefined,
        driverCode: selectedHandover.driverCode || undefined,
        driverName: selectedHandover.driverName || undefined,
        clientID: Number(selectedHandover.clientID) || 0,
      }));
    }
  }, [selectedHandover]);

  // Filter cost bearer options based on available data in selected handover
  const availableCostBearerOptions = useMemo(() => {
    const allOptions = [
      { value: 'MITRA', label: 'Mitra' },
      { value: 'CLIENT', label: 'Client' },
      { value: 'OEM', label: 'OEM' },
      { value: 'DASH_ELECTRIC', label: 'Dash' },
    ];

    if (!selectedHandover) return allOptions;

    return allOptions.filter((option) => {
      // Always allow DASH_ELECTRIC
      if (option.value === 'DASH_ELECTRIC') return true;

      // Only show MITRA if handover has driver/mitra data
      if (option.value === 'MITRA') {
        return !!(selectedHandover.driverName || selectedHandover.driverCode);
      }

      // Only show CLIENT if handover has client data
      if (option.value === 'CLIENT') {
        return !!(selectedHandover.clientName || selectedHandover.client);
      }

      // Only show OEM if handover has OEM data
      if (option.value === 'OEM') {
        return !!(selectedHandover.oemName || selectedHandover.oemID);
      }

      return true;
    });
  }, [selectedHandover]);

  // Auto-clear costBearer if the selected value is no longer available
  useEffect(() => {
    if (
      formData.costBearer &&
      !availableCostBearerOptions.some(
        (opt) => opt.value === formData.costBearer
      )
    ) {
      setFormData((prev) => ({ ...prev, costBearer: '' }));
    }
  }, [availableCostBearerOptions, formData.costBearer]);

  // Reset costBearer when handover changes
  useEffect(() => {
    if (formData.handoverID) {
      setFormData((prev) => ({ ...prev, costBearer: '' }));
    }
  }, [formData.handoverID]);

  // Dynamic label & value for Cost Bearer
  const costBearerDetail = useMemo(() => {
    if (!formData.costBearer || formData.costBearer === 'DASH_ELECTRIC')
      return null;

    const details: Record<
      string,
      { label: string; value: string | undefined | null }
    > = {
      MITRA: {
        label: 'Mitra Name',
        value: selectedHandover
          ? `${selectedHandover.driverCode || ''} - ${selectedHandover.driverName || 'No Name'}`
          : null,
      },
      CLIENT: {
        label: 'Client Name',
        value: selectedHandover?.clientName || selectedHandover?.client,
      },
      OEM: {
        label: 'OEM Name',
        value: selectedHandover?.oemName,
      },
    };

    return details[formData.costBearer];
  }, [formData.costBearer, selectedHandover]);

  // Handle File Upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData((prev) => ({
        ...prev,
        photos: [...prev.photos, ...newFiles],
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    if (bytes < 1024) return bytes + ' B';
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(0)) +
      ' ' +
      sizes[i - 1 < 0 ? 0 : i - 1]
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        contentRef={scrollRef}
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Report Issues
            </h2>
            <p className="text-sm font-normal text-gray-600">
              New Issues details.
            </p>
            <div className="flex justify-center pb-2 mt-3">
              <StepIndicator
                steps={[
                  { id: 1, label: 'General Data' },
                  { id: 2, label: 'Details' },
                ]}
                currentStep={currentStep}
              />
            </div>
          </div>
        }
        footer={
          currentStep === 1 ? (
            <Button
              variant="primary"
              onClick={handleNext}
              className="w-full py-3"
              disabled={
                !formData.handoverID ||
                !formData.issueType ||
                !formData.incidentDate ||
                !formData.severity ||
                !formData.costBearer
              }
            >
              Next
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="neutral"
                onClick={handleBack}
                className="flex-1 py-3"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="flex-1 py-3"
              >
                {isSaving || isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )
        }
      >
        <div className="flex-1">
          <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 heading-field">
            {currentStep === 1 ? 'General Data' : 'Details'}
          </div>

          <div className="p-6 pb-40">
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Plate Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plate Number <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={handoverOptions}
                    value={formData.handoverID}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, handoverID: val }))
                    }
                    placeholder={
                      isLoadingData
                        ? 'Loading...'
                        : 'e.g. B 1234 ABC - Electrum - H3'
                    }
                    disabled={isLoadingData}
                    className="w-full"
                  />
                </div>

                {/* Issue Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Type <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={issueTypeOptions}
                    value={formData.issueType}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, issueType: val }))
                    }
                    placeholder="e.g. Kerusakan Unit Akibat Kecelakaan"
                    className="w-full"
                  />
                </div>

                {/* Tanggal Kejadian */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tanggal Kejadian <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${!formData.incidentDate ? 'text-gray-400' : 'text-gray-900'}`}
                    value={formData.incidentDate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        incidentDate: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={severityOptions}
                    value={formData.severity}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        severity: val as IssueSeverity,
                      }))
                    }
                    placeholder="e.g. Medium"
                    className="w-full"
                  />
                </div>

                {/* Estimasi Biaya */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimasi Biaya <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">Rp</span>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      placeholder="0"
                      value={idrDisplayCost}
                      onChange={(e) => handleCostChange(e.target.value)}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                      <img
                        src="https://flagcdn.com/w40/id.png"
                        alt="IDR"
                        className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                      />
                      <span className="text-gray-500 text-sm">IDR</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Isi 0 jika tidak ada biaya.
                  </p>
                </div>

                {/* Biaya Ditanggung */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Ditanggung <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={availableCostBearerOptions}
                    value={formData.costBearer}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, costBearer: val }))
                    }
                    placeholder="e.g. Mitra"
                    className="w-full"
                  />
                </div>

                {/* Dynamic Cost Bearer Details */}
                {costBearerDetail && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {costBearerDetail.label}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                      {costBearerDetail.value || 'Select Plate Number first'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto
                  </label>
                  {formData.photos.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={handleFileChange}
                      />
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-900 font-medium">
                        Choose a file or drag & drop it here.
                      </p>
                      <div className="mt-4">
                        <span className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-white">
                          Browse File
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.photos.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-xl bg-white"
                        >
                          <div className="flex items-center space-x-4">
                            {/* Document Icon */}
                            <div className="w-12 h-12 flex-shrink-0 relative">
                              <svg
                                viewBox="0 0 40 40"
                                fill="none"
                                className="w-full h-full"
                              >
                                {/* Paper Background */}
                                <path
                                  d="M12 4H24L32 12V34C32 35.1046 31.1046 36 30 36H12C10.8954 36 10 35.1046 10 34V6C10 4.89543 10.8954 4 12 4Z"
                                  fill="white"
                                  stroke="#9CA3AF"
                                  strokeWidth="2"
                                  strokeLinejoin="round"
                                />
                                {/* Folded Corner */}
                                <path
                                  d="M24 4V10C24 11.1046 24.8954 12 26 12H32"
                                  stroke="#9CA3AF"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                {/* JPG Badge */}
                                <rect
                                  x="6"
                                  y="20"
                                  width="28"
                                  height="13"
                                  rx="4"
                                  fill="#6B7280"
                                />
                                <text
                                  x="20"
                                  y="29.5"
                                  textAnchor="middle"
                                  fill="white"
                                  fontSize="9"
                                  fontWeight="bold"
                                  fontFamily="sans-serif"
                                >
                                  JPG
                                </text>
                              </svg>
                            </div>

                            {/* File Info */}
                            <div>
                              <p className="text-base font-medium text-gray-900 truncate max-w-[200px]">
                                {file.name}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                <span>
                                  {formatFileSize(file.size)} of{' '}
                                  {formatFileSize(file.size)}
                                </span>
                                <span>•</span>
                                <span className="text-green-500 flex items-center gap-1 font-medium">
                                  <i className="ri-checkbox-circle-fill"></i>
                                  Completed
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-gray-500 hover:text-red-500 transition-colors"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>
                        </div>
                      ))}
                      {/* Add more photos removed for single file limit */}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <div className="relative">
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                      rows={4}
                      maxLength={200}
                      placeholder="e.g. Motor ini bekas kecelakaan"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                    />
                    <div className="absolute bottom-3 right-2 text-xs text-gray-400">
                      {formData.notes.length}/200{' '}
                      <i className="ri-draggable"></i>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SideBarModal>
    </>
  );
};
